import { createFileRoute, redirect } from "@tanstack/react-router";
import { VolumeApi } from "@/lib/volume-api";
import { CheckCircle, XCircle } from "lucide-react";
import { DisableApplicationSecret } from "@/routes/(app)/_main-layout/_settings/-components/security/disable-application-secret";
import { ResetApplicationSecret } from "@/routes/(app)/_main-layout/_settings/-components/security/reset-application-secret";
import { hasRole } from "@/lib/auth";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { CardWithHeader } from "@/components/card-with-header";
import { Separator } from "@/components/ui/separator";

export const Route = createFileRoute(
  "/(app)/_main-layout/_settings/settings/security",
)({
  component: () => <SecurityComponent />,
  beforeLoad({ context }) {
    if (!hasRole("EDIT_SETTINGS", context.authorities)) {
      throw redirect({ to: "/settings/account-settings", search: true });
    }
  },
  loader: ({ context: { selectedApplicationId, accessToken, merchantId } }) => {
    const api = new VolumeApi(accessToken, selectedApplicationId);
    return api.getApplicationSecretSummary({
      merchantId: merchantId,
    });
  },
});

function SecurityComponent() {
  const applicationSecretSummary = Route.useLoaderData();
  const application = useSelectedApplication();
  return (
    <CardWithHeader title="Application keys">
      <div className="grid grid-cols-12  gap-4 bg-white px-1 text-sm">
        <div className="col-span-2 text-black">Application ID</div>
        <div className="col-span-9 text-volume-dark-grey">
          {application.applicationId}
        </div>
      </div>
      <Separator className="my-4" />
      <div className="grid grid-cols-12  gap-4 bg-white px-1 text-sm items-center">
        <div className="col-span-2 text-black">Primary private key</div>
        <div className="col-span-8 text-volume-dark-grey">
          {applicationSecretSummary.primarySecretEnabled && (
            <span className={"flex items-center"}>
              <CheckCircle className="h-6 w-6 mr-2" />
              Enabled
            </span>
          )}
          {!applicationSecretSummary.primarySecretEnabled && (
            <span className={"flex items-center"}>
              <XCircle className="h-6 w-6 mr-2" /> Disabled
            </span>
          )}
        </div>
        <div className="col-span-2 flex text-volume-dark-grey justify-end">
          <DisableApplicationSecret
            secretType={"PRIMARY"}
            classes={"mr-1"}
            currentValueIsEnabled={
              applicationSecretSummary.primarySecretEnabled
            }
          />
          <ResetApplicationSecret secretType={"PRIMARY"} />
        </div>
      </div>
      <Separator className="my-4" />
      <div className="grid grid-cols-12  gap-4 bg-white px-1 text-sm items-center">
        <div className="col-span-2 text-black">Secondary private key</div>
        <div className="col-span-8 text-volume-dark-grey">
          {applicationSecretSummary.secondarySecretEnabled && (
            <span className={"flex  items-center"}>
              <CheckCircle className="h-6 w-6 mr-2" /> Enabled
            </span>
          )}
          {!applicationSecretSummary.secondarySecretEnabled && (
            <span className={"flex items-center"}>
              <XCircle className="h-6 w-6 mr-2" /> Disabled
            </span>
          )}
        </div>
        <div className="col-span-2 flex text-volume-dark-grey justify-end">
          <DisableApplicationSecret
            secretType={"SECONDARY"}
            classes={"mr-1"}
            currentValueIsEnabled={
              applicationSecretSummary.secondarySecretEnabled
            }
          />
          <ResetApplicationSecret secretType={"SECONDARY"} />
        </div>
      </div>
    </CardWithHeader>
  );
}
